/**
 * Visually-hidden screen-reader mirror for the 3D constellation.
 *
 * Sighted users see the R3F canvas. AT users hear this tree. Both surfaces
 * stay in sync because both consume the same focused-entity state.
 * Click-to-focus is wired so the mirror also doubles as the keyboard /
 * reduced-motion fallback.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
import { buildA11yMirror } from './a11yMirror.js';

export interface ConstellationMirrorProps {
  manifest: AtlasManifest;
  focusedId: string | undefined;
  onFocus: (entityId: string) => void;
}

export function ConstellationMirror({
  manifest,
  focusedId,
  onFocus,
}: ConstellationMirrorProps): React.JSX.Element {
  const mirror = buildA11yMirror(manifest, focusedId ?? null);
  return (
    <div className="constellation-mirror">
      <div role="status" aria-live="polite">
        {mirror.summary}
      </div>
      <nav aria-label="3D constellation — entities by domain">
        <ul>
          {mirror.domains.map((d) => (
            <li key={d.id}>
              <h3>{d.name}</h3>
              <ul>
                {d.entities.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => onFocus(e.id)}
                      aria-current={e.focused ? 'true' : undefined}
                    >
                      {e.name}
                    </button>
                    {(e.outgoing.length > 0 || e.incoming.length > 0) && (
                      <ul>
                        {e.outgoing.map((r) => (
                          <li key={`o-${r.fromField}-${r.to}`}>
                            → {r.to} (as {r.fromField})
                          </li>
                        ))}
                        {e.incoming.map((r) => (
                          <li key={`i-${r.from}-${r.fromField}`}>
                            ← {r.from} (via {r.fromField})
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
